<template>
	<AmeliproDialog
		v-model="dialog"
		labelledby="structure-menu-title"
		unique-id="structure-menu"
		width="800"
		@change="emitChangeEvent(false)"
		@confirm="emitConfirmEvent"
	>
		<template #header>
			<h2
				id="structure-menu-title"
				class="ma-0 text-h4"
			>
				{{ locales.title }}
			</h2>
		</template>

		<template #default>
			<div class="py-3">
				<div class="d-flex flex-column flex-md-row justify-md-space-between">
					<div>
						<p class="ma-0 body-1 font-weight-semibold">
							{{ userName }}
						</p>

						<p class="ma-0 body-2 font-weight-semibold">
							{{ userProfession }}
						</p>

						<p class="ma-0 body-2 mb-5">
							{{ locales.labelRPPS }} {{ userRpps }} {{ locales.labelAdeli }} {{ userAdeli }}
						</p>
					</div>

					<AmeliproMessage
						align-start
						class="message-width"
						text
						type="info"
					>
						<p class="mb-0">
							{{ locales.infoMessage }}
						</p>
					</AmeliproMessage>
				</div>

				<StructureTabs
					v-model="selectedStructure"
					aria-labelledby="structure-menu-title"
					:items="items"
					:max-items-loaded-default="maxItemsLoadedDefault"
				>
					<template #searchBar>
						bloc de recherche
					</template>
				</StructureTabs>

				<div class="d-flex justify-end align-start mt-2">
					<AmeliproSwitch
						id="switch-structure-defaut"
						v-model="switchModel"
						aria-describedby="default-structure-switch-description"
						hide-error-message
						:label="locales.switchLabel"
						label-left
					/>

					<AmeliproTooltips
						id="default-structure-switch-description"
						class="ml-2 mt-1"
						:tooltip-text="locales.switchDescription"
					/>
				</div>
			</div>
		</template>

		<template #footer>
			<div class="d-flex flex-column flex-sm-row justify-sm-space-between">
				<AmeliproBtn
					class="order-sm-1"
					@click="emitConfirmEvent"
				>
					{{ locales.confirmLabel }}
				</AmeliproBtn>

				<AmeliproBtn
					bordered
					class="mt-2 mt-sm-0 order-sm-0"
					color="ap-white"
					hover-color="ap-blue lighten-3"
					text-color="ap-blue darken-1"
					@click="emitChangeEvent(false)"
				>
					{{ locales.cancelLabel }}
				</AmeliproBtn>
			</div>
		</template>
	</AmeliproDialog>
</template>

<script setup lang="ts">
	import { IStructureTabs, StructureTab } from '../StructureTabs/types';
	import { PropType, computed, ref } from 'vue';
	import AmeliproBtn from '../AmeliproBtn/AmeliproBtn.vue';
	import AmeliproDialog from '../AmeliproDialog/AmeliproDialog.vue';
	import AmeliproMessage from '../AmeliproMessage/AmeliproMessage.vue';
	import AmeliproSwitch from '../AmeliproSwitch/AmeliproSwitch.vue';
	import AmeliproTooltips from '../AmeliproTooltips/AmeliproTooltips.vue';
	import { IStructureMenuValue } from './types';
	import StructureTabs from '../StructureTabs/StructureTabs.vue';
	import { locales } from './locales';

	const props = defineProps({
		items: {
			type: Array as PropType<StructureTab[]>,
			default: () => [],
		},
		maxItemsLoadedDefault: {
			type: Number,
			default: 5,
		},
		userAdeli: {
			type: String,
			required: true,
		},
		userName: {
			type: String,
			required: true,
		},
		userProfession: {
			type: String,
			required: true,
		},
		userRpps: {
			type: String,
			required: true,
		},
		value: {
			type: Object as PropType<IStructureMenuValue>,
			default: () => ({ dialog: false }),
		},
	});

	const switchModel = ref(false);

	const emit = defineEmits(['change', 'input', 'validate']);
	const emitChangeEvent = (value : boolean) : void => {
		dialog.value = value;
		emit('change', dialog.value);
	};

	const emitConfirmEvent = () : void => {
		emit('validate', {
			defaultStructure: switchModel.value,
			selected: selectedStructure.value,
		});
		emitChangeEvent(false);
	};

	const selectedStructure = computed({
		get() {
			return { activeTab: props.value.activeTab, activeValues: props.value.activeValues };
		},
		set(value: IStructureTabs) {
			emit('input', { ...props.value, ...value });
		},
	});

	const dialog = computed({
		get() {
			return props.value.dialog;
		},
		set(value: boolean) {
			emit('input', { ...props.value, dialog: value });
		},
	});
</script>

<style lang="scss" scoped>
	.v-input.amelipro-switch {
		margin-top: 0;
	}

	.message-width {
		width: 50% !important;

		@media #{$media-down-md} {
			width: 100% !important;
		}
	}
</style>
